import { useState, useEffect, useMemo } from 'react';
import ModalBase from '@/components/base/ModalBase';
import type { InvoiceItem } from '@/mocks/superAdminInvoices';

interface InvoiceFormModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: InvoiceFormData) => void;
}

export interface InvoiceFormData {
  radioName: string;
  frequency: string;
  planName: string;
  concepto: string;
  items: InvoiceItem[];
  discount: number;
  taxRate: string;
  dueDate: string;
  notes: string;
}

function newEmptyItem(): InvoiceItem {
  return {
    id: `it-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    type: 'plan_mensual',
    description: '',
    quantity: 1,
    unitPrice: 0,
    total: 0,
  };
}

function computeItemTotal(qty: number, price: number): number {
  return Math.round(qty * price * 100) / 100;
}

interface RadioOption {
  name: string;
  frequency: string;
  plan: string;
}

const RADIO_OPTIONS: RadioOption[] = [
  { name: 'Stereo Visión', frequency: '94.70 RFW', plan: 'Pro' },
  { name: 'Radio Nueva Actitud', frequency: '87.90 RFW', plan: 'Premium' },
  { name: 'TechBeats', frequency: '88.50 RFW', plan: 'Pro' },
  { name: 'Radio Élite Digital', frequency: '91.30 RFW', plan: 'Business' },
  { name: 'Radio Deportiva Total', frequency: '101.70 RFW', plan: 'Pro' },
  { name: 'Radio Urbana 101', frequency: '103.50 RFW', plan: 'Premium' },
  { name: 'Radio Éxitos TV', frequency: '100.00 RFW', plan: 'Business' },
  { name: 'Radio Folclor Andino', frequency: '107.90 RFW', plan: 'Básico' },
  { name: 'Jazz Club Radio', frequency: '92.80 RFW', plan: 'Pro' },
  { name: 'Radio Marítima', frequency: '89.50 RFW', plan: 'Starter' },
  { name: 'Radio Sonora Digital', frequency: '96.20 RFW', plan: 'Starter' },
  { name: 'Clásica FM', frequency: '99.10 RFW', plan: 'Básico' },
  { name: 'Radio Retro Hits', frequency: '93.10 RFW', plan: 'Premium' },
  { name: 'Radio Vibra FM', frequency: '90.80 RFW', plan: 'Business' },
  { name: 'Radio Fusión Latina', frequency: '105.30 RFW', plan: 'Básico' },
];

const PLAN_OPTIONS = ['Starter', 'Básico', 'Pro', 'Business', 'Premium'];
const IVA_OPTIONS = ['16%', '21%', '0%'];

export default function InvoiceFormModal({ open, onClose, onSave }: InvoiceFormModalProps) {
  const [step, setStep] = useState(1);

  // Step 1
  const [selectedRadio, setSelectedRadio] = useState<RadioOption | null>(null);
  const [customRadio, setCustomRadio] = useState('');
  const [customFreq, setCustomFreq] = useState('');
  const [planName, setPlanName] = useState('Pro');
  const [concepto, setConcepto] = useState('');

  // Step 2
  const [items, setItems] = useState<InvoiceItem[]>([newEmptyItem()]);
  const [discount, setDiscount] = useState('0');
  const [taxRate, setTaxRate] = useState('16%');

  // Step 3
  const [dueDate, setDueDate] = useState('');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (!open) return;
    setStep(1);
    setSelectedRadio(null);
    setCustomRadio('');
    setCustomFreq('');
    setPlanName('Pro');
    setConcepto('');
    setItems([newEmptyItem()]);
    setDiscount('0');
    setTaxRate('16%');
    setDueDate('');
    setNotes('');
  }, [open]);

  const addItem = () => setItems((prev) => [...prev, newEmptyItem()]);
  const removeItem = (idx: number) => {
    if (items.length <= 1) return;
    setItems((prev) => prev.filter((_, i) => i !== idx));
  };

  const updateItem = (idx: number, field: keyof InvoiceItem, value: string | number) => {
    setItems((prev) => prev.map((it, i) => {
      if (i !== idx) return it;
      const updated = { ...it, [field]: value };
      if (field === 'quantity' || field === 'unitPrice') {
        updated.total = computeItemTotal(updated.quantity, updated.unitPrice);
      }
      return updated;
    }));
  };

  const discountNum = parseFloat(discount) || 0;
  const taxRateNum = parseFloat(taxRate) / 100;
  const subtotal = items.reduce((s, it) => s + it.total, 0);

  const formTotal = useMemo(() => {
    const afterDiscount = Math.max(0, subtotal - discountNum);
    const tax = Math.round(afterDiscount * taxRateNum * 100) / 100;
    return afterDiscount + tax;
  }, [subtotal, discountNum, taxRateNum]);

  const radioName = selectedRadio ? selectedRadio.name : customRadio;
  const frequency = selectedRadio ? selectedRadio.frequency : customFreq;

  const canGoNext1 = (selectedRadio || (customRadio.trim() && customFreq.trim()));
  const validItems = items.filter((it) => it.description.trim());
  const canGoNext2 = validItems.length > 0 && formTotal >= 0;
  const canConfirm = !!dueDate;

  const handleConfirm = () => {
    onSave({
      radioName: radioName.trim(),
      frequency: frequency.trim(),
      planName,
      concepto: concepto.trim() || `Plan ${planName}`,
      items: validItems,
      discount: discountNum,
      taxRate,
      dueDate: dueDate || new Date(Date.now() + 14 * 86400000).toISOString().slice(0, 10),
      notes: notes.trim(),
    });
  };

  const steps = [
    { num: 1, label: 'Radio', icon: 'ri-radio-line' },
    { num: 2, label: 'Conceptos', icon: 'ri-list-check-3' },
    { num: 3, label: 'Confirmar', icon: 'ri-check-double-line' },
  ];

  return (
    <ModalBase
      open={open}
      onClose={onClose}
      title="Nueva Factura"
      subtitle="Creá una factura en 3 pasos"
      size="md"
    >
      <div className="space-y-5">
        {/* Steps indicator */}
        <div className="flex items-center gap-2">
          {steps.map((s, i) => (
            <div key={s.num} className="flex items-center gap-2 flex-1">
              <button
                onClick={() => { if (s.num < step) setStep(s.num); }}
                className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-[10px] font-semibold transition-colors cursor-pointer whitespace-nowrap ${step === s.num ? 'bg-foreground-950 text-background-50' : step > s.num ? 'bg-emerald-100 text-emerald-700' : 'bg-background-100 text-foreground-400'}`}
              >
                <i className={`${s.icon} text-[11px]`}></i>
                {s.label}
              </button>
              {i < steps.length - 1 && (
                <div className={`h-px flex-1 ${step > s.num ? 'bg-emerald-300' : 'bg-background-200'}`}></div>
              )}
            </div>
          ))}
        </div>

        {/* ═══ STEP 1 — Radio ═══ */}
        {step === 1 && (
          <div className="space-y-4">
            <p className="text-foreground-500 text-xs">Paso 1 — Seleccioná la radio</p>

            {/* Quick select */}
            <div>
              <p className="text-foreground-400 text-[10px] font-medium mb-2">Rápido</p>
              <div className="grid grid-cols-1 gap-1 max-h-[200px] overflow-y-auto bg-background-100 rounded-xl p-1">
                {RADIO_OPTIONS.map((r) => (
                  <button
                    key={r.name}
                    onClick={() => { setSelectedRadio(r); setPlanName(r.plan); }}
                    className={`flex items-center justify-between px-3 py-2 rounded-lg text-left transition-colors cursor-pointer ${selectedRadio?.name === r.name ? 'bg-foreground-950 text-background-50' : 'hover:bg-background-200 text-foreground-800'}`}
                  >
                    <div>
                      <p className="text-xs font-semibold">{r.name}</p>
                      <p className={`text-[10px] ${selectedRadio?.name === r.name ? 'text-background-400' : 'text-foreground-400'}`}>{r.frequency}</p>
                    </div>
                    <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${selectedRadio?.name === r.name ? 'bg-background-200/30 text-background-100' : 'bg-background-200 text-foreground-500'}`}>{r.plan}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="flex items-center gap-2">
              <div className="flex-1 h-px bg-background-200"></div>
              <span className="text-foreground-400 text-[10px] whitespace-nowrap">o personalizado</span>
              <div className="flex-1 h-px bg-background-200"></div>
            </div>

            {/* Custom */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-foreground-400 text-[10px] font-medium block mb-1">Nombre de la radio</label>
                <input
                  type="text" value={customRadio} onChange={(e) => { setCustomRadio(e.target.value); setSelectedRadio(null); }}
                  placeholder="Ej: Radio Fuego"
                  className="w-full px-3 py-2 bg-background-100 border border-background-200 rounded-lg text-xs text-foreground-800 outline-none focus:border-foreground-300 transition-colors"
                />
              </div>
              <div>
                <label className="text-foreground-400 text-[10px] font-medium block mb-1">Frecuencia RFW</label>
                <input
                  type="text" value={customFreq} onChange={(e) => { setCustomFreq(e.target.value); setSelectedRadio(null); }}
                  placeholder="99.90 RFW"
                  className="w-full px-3 py-2 bg-background-100 border border-background-200 rounded-lg text-xs text-foreground-800 outline-none focus:border-foreground-300 transition-colors"
                />
              </div>
            </div>

            {/* Plan — pill buttons */}
            <div>
              <label className="text-foreground-400 text-[10px] font-medium block mb-1.5">Plan</label>
              <div className="flex flex-wrap items-center gap-1">
                {PLAN_OPTIONS.map((p) => (
                  <button
                    key={p}
                    onClick={() => setPlanName(p)}
                    className={`px-3 py-1.5 rounded-full text-[10px] font-semibold transition-colors cursor-pointer whitespace-nowrap ${planName === p ? 'bg-foreground-950 text-background-50' : 'bg-background-100 text-foreground-500 hover:bg-background-200 border border-background-200'}`}
                  >
                    {p}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="text-foreground-400 text-[10px] font-medium block mb-1">Concepto</label>
              <input type="text" value={concepto} onChange={(e) => setConcepto(e.target.value)} placeholder="Plan Pro — Servicios" className="w-full px-3 py-2 bg-background-100 border border-background-200 rounded-lg text-xs text-foreground-800 outline-none focus:border-foreground-300 transition-colors" />
            </div>
          </div>
        )}

        {/* ═══ STEP 2 — Conceptos ═══ */}
        {step === 2 && (
          <div className="space-y-4">
            <p className="text-foreground-500 text-xs">Paso 2 — Agregá los conceptos</p>

            <div className="space-y-2">
              {items.map((it, idx) => (
                <div key={it.id} className="bg-background-100 rounded-xl p-3">
                  <div className="flex items-center gap-2 mb-2">
                    <input
                      type="text" value={it.description} onChange={(e) => updateItem(idx, 'description', e.target.value)}
                      placeholder="Descripción del concepto"
                      className="flex-1 px-3 py-1.5 bg-background-50 border border-background-200 rounded-lg text-xs text-foreground-800 outline-none focus:border-foreground-300 transition-colors"
                    />
                    {items.length > 1 && (
                      <button onClick={() => removeItem(idx)} className="w-6 h-6 rounded-md bg-red-50 flex items-center justify-center cursor-pointer hover:bg-red-100 transition-colors shrink-0" title="Quitar ítem">
                        <i className="ri-close-line text-red-400 text-[10px]"></i>
                      </button>
                    )}
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <label className="text-foreground-400 text-[9px] block mb-0.5">Cant.</label>
                      <input type="number" value={it.quantity} onChange={(e) => updateItem(idx, 'quantity', parseInt(e.target.value) || 1)} min="1" className="w-full px-2 py-1.5 bg-background-50 border border-background-200 rounded-lg text-xs text-foreground-800 outline-none focus:border-foreground-300 transition-colors" />
                    </div>
                    <div>
                      <label className="text-foreground-400 text-[9px] block mb-0.5">P. Unit.</label>
                      <input type="number" value={it.unitPrice} onChange={(e) => updateItem(idx, 'unitPrice', parseFloat(e.target.value) || 0)} min="0" step="0.01" className="w-full px-2 py-1.5 bg-background-50 border border-background-200 rounded-lg text-xs text-foreground-800 outline-none focus:border-foreground-300 transition-colors" />
                    </div>
                    <div>
                      <label className="text-foreground-400 text-[9px] block mb-0.5">Total</label>
                      <div className="w-full px-2 py-1.5 bg-background-50 border border-background-200 rounded-lg text-xs text-foreground-800 font-semibold">${it.total.toFixed(2)}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <button onClick={addItem} className="w-full py-2 rounded-xl border border-dashed border-background-300 text-foreground-400 text-xs font-medium hover:bg-background-100 hover:text-foreground-600 transition-colors cursor-pointer whitespace-nowrap">
              <i className="ri-add-line text-sm mr-1"></i>Agregar concepto
            </button>

            {/* Totals */}
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="text-foreground-400 text-[10px] font-medium block mb-1">Descuento</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-xs">$</span>
                  <input type="number" value={discount} onChange={(e) => setDiscount(e.target.value)} min="0" step="0.01" className="w-full pl-7 pr-3 py-2 bg-background-100 border border-background-200 rounded-lg text-xs text-foreground-800 outline-none focus:border-foreground-300 transition-colors" />
                </div>
              </div>
              <div>
                <label className="text-foreground-400 text-[10px] font-medium block mb-1.5">IVA</label>
                <div className="flex items-center gap-1">
                  {IVA_OPTIONS.map((iva) => (
                    <button
                      key={iva}
                      onClick={() => setTaxRate(iva)}
                      className={`flex-1 px-2 py-2 rounded-full text-[10px] font-semibold transition-colors cursor-pointer whitespace-nowrap ${taxRate === iva ? 'bg-foreground-950 text-background-50' : 'bg-background-100 text-foreground-500 hover:bg-background-200 border border-background-200'}`}
                    >
                      {iva}
                    </button>
                  ))}
                </div>
              </div>
              <div className="bg-background-100 rounded-xl p-3 flex flex-col justify-center">
                <p className="text-foreground-400 text-[9px] font-medium">Total</p>
                <p className="text-foreground-800 text-sm font-bold">${formTotal.toFixed(2)}</p>
              </div>
            </div>
          </div>
        )}

        {/* ═══ STEP 3 — Confirm ═══ */}
        {step === 3 && (
          <div className="space-y-4">
            <p className="text-foreground-500 text-xs">Paso 3 — Confirmá y creá</p>

            {/* Summary */}
            <div className="bg-background-100 rounded-xl p-4 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-foreground-400 text-[10px]">Radio</span>
                <div className="text-right">
                  <p className="text-foreground-800 text-xs font-semibold">{radioName}</p>
                  <p className="text-foreground-400 text-[10px]">{frequency}</p>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-foreground-400 text-[10px]">Plan</span>
                <span className="text-foreground-800 text-xs font-semibold">{planName}</span>
              </div>
              {concepto && (
                <div className="flex items-center justify-between">
                  <span className="text-foreground-400 text-[10px]">Concepto</span>
                  <span className="text-foreground-800 text-xs font-semibold max-w-[200px] text-right">{concepto}</span>
                </div>
              )}
              <div className="border-t border-background-200 pt-3">
                <p className="text-foreground-400 text-[10px] font-medium mb-2">{validItems.length} concepto{validItems.length !== 1 ? 's' : ''}</p>
                {validItems.map((it, i) => (
                  <div key={it.id} className="flex items-center justify-between text-xs mb-1">
                    <span className="text-foreground-700">{it.description}</span>
                    <span className="text-foreground-800 font-semibold">${it.total.toFixed(2)}</span>
                  </div>
                ))}
              </div>
              <div className="border-t border-background-200 pt-2 space-y-1">
                <div className="flex items-center justify-between text-[10px]">
                  <span className="text-foreground-400">Subtotal</span>
                  <span className="text-foreground-700">${subtotal.toFixed(2)}</span>
                </div>
                {discountNum > 0 && (
                  <div className="flex items-center justify-between text-[10px]">
                    <span className="text-foreground-400">Descuento</span>
                    <span className="text-emerald-600">-${discountNum.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex items-center justify-between text-[10px]">
                  <span className="text-foreground-400">IVA ({taxRate})</span>
                  <span className="text-foreground-700">${(Math.round((formTotal - Math.max(0, subtotal - discountNum)) * 100) / 100).toFixed(2)}</span>
                </div>
                <div className="flex items-center justify-between border-t border-foreground-200 pt-1.5">
                  <span className="text-foreground-800 text-xs font-bold">TOTAL</span>
                  <span className="text-foreground-800 text-sm font-bold">${formTotal.toFixed(2)}</span>
                </div>
              </div>
            </div>

            {/* Dates & Notes */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-foreground-400 text-[10px] font-medium block mb-1">Vencimiento *</label>
                <input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} className="w-full px-3 py-2 bg-background-100 border border-background-200 rounded-lg text-xs text-foreground-800 outline-none focus:border-foreground-300 transition-colors cursor-pointer" />
              </div>
              <div>
                <label className="text-foreground-400 text-[10px] font-medium block mb-1">Notas</label>
                <textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Notas internas..." rows={2} className="w-full px-3 py-2 bg-background-100 border border-background-200 rounded-lg text-xs text-foreground-800 outline-none focus:border-foreground-300 transition-colors resize-none" />
              </div>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-between pt-3 border-t border-background-200">
          <div>
            {step > 1 && (
              <button onClick={() => setStep(step - 1)} className="px-4 py-2 rounded-xl bg-background-100 text-foreground-600 text-xs font-medium hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap">
                <i className="ri-arrow-left-line text-sm mr-1"></i>Atrás
              </button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button onClick={onClose} className="px-4 py-2 rounded-xl bg-background-50 text-foreground-500 text-xs font-medium hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap">Cancelar</button>
            {step < 3 ? (
              <button
                onClick={() => setStep(step + 1)}
                disabled={(step === 1 && !canGoNext1) || (step === 2 && !canGoNext2)}
                className="px-4 py-2 rounded-xl bg-foreground-950 text-background-50 text-xs font-bold hover:bg-foreground-800 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Siguiente
                <i className="ri-arrow-right-line text-sm ml-1"></i>
              </button>
            ) : (
              <button
                onClick={handleConfirm}
                disabled={!canConfirm}
                className="px-4 py-2 rounded-xl bg-foreground-950 text-background-50 text-xs font-bold hover:bg-foreground-800 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <i className="ri-check-line text-sm mr-1"></i>Crear Factura
              </button>
            )}
          </div>
        </div>
      </div>
    </ModalBase>
  );
}